import { useMemo } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import { providerAdminApi } from '@/api/providerAdmin';
import { useAuthStore } from '@/store/authStore';

export type ProviderSegment = 'solo' | 'team' | 'salon' | 'shop' | 'unknown';

/**
 * Computed segment + capability flags for the currently-signed-in provider
 * admin. Drives sidebar filtering, onboarding-checklist content, and any
 * other UI that should branch on what kind of business this is.
 *
 * The segment is derived heuristically from the modules the provider has
 * specialties in:
 *   - shop module present       → 'shop'        (storefront-first)
 *   - booking module present    → 'salon'       (appointments-first)
 *   - has team members          → 'team'        (delivery / home-service co.)
 *   - default                   → 'solo'        (1-person operator)
 *
 * "Unknown" only fires when no exposure rows exist yet — signals we haven't
 * loaded yet OR the provider hasn't picked any specialty.
 */
export interface ProviderSegmentInfo {
  segment: ProviderSegment;
  modules: string[];
  hasShop: boolean;
  hasBooking: boolean;
  hasHomeServices: boolean;
  hasOnlineServices: boolean;
  hasTeam: boolean;
  hasSpecialties: boolean;
  /** True when at least one service exists across the provider's specialties.
   * Drives onboarding-checklist completion for service-style segments. */
  hasServices: boolean;
  /** True when at least one product exists in any shop specialty.
   * Drives onboarding-checklist completion for shops. */
  hasProducts: boolean;
  workerIds: number[];
  isLoading: boolean;
  isError: boolean;
  ready: boolean;
}

export function useProviderSegment(): ProviderSegmentInfo {
  const { role, user } = useAuthStore();
  const isProviderAdmin = role === 'provideradmin';

  const { data: exposures = [], isLoading: loadingExp, isError: exposureError, isFetched: exposureFetched } = useQuery({
    queryKey: ['provider-business-exposure'],
    queryFn: () => providerAdminApi.getBusinessExposures(),
    enabled: isProviderAdmin && !!user,
    staleTime: 60_000,
  });

  const { data: team = [], isLoading: loadingTeam, isError: teamError, isFetched: teamFetched } = useQuery({
    queryKey: ['provider-team', user?.userId],
    queryFn: () => providerAdminApi.getTeamMembers(user!.userId),
    enabled: isProviderAdmin && !!user,
    staleTime: 60_000,
  });

  // Services / products counts per specialty. Run in parallel — typical
  // providers have 1-3 specialties so this is cheap. Used by the onboarding
  // checklist to flip the "add services" / "add products" steps to done.
  const serviceSpecialtyIds = exposures
    .filter((e) => e.moduleKey !== 'shop')
    .map((e) => e.specialtyId);
  const shopSpecialtyIds = exposures
    .filter((e) => e.moduleKey === 'shop')
    .map((e) => e.specialtyId);

  const serviceQueries = useQueries({
    queries: serviceSpecialtyIds.map((id) => ({
      queryKey: ['provider-services-count', id] as const,
      queryFn: () => providerAdminApi.getServicesBySpecialty(id),
      enabled: isProviderAdmin && !!user,
      staleTime: 60_000,
    })),
  });

  const productQueries = useQueries({
    queries: shopSpecialtyIds.map((id) => ({
      queryKey: ['provider-products-count', user?.userId, id] as const,
      queryFn: () => providerAdminApi.getProductsBySpecialty(user!.userId, id, 1),
      enabled: isProviderAdmin && !!user,
      staleTime: 60_000,
    })),
  });

  return useMemo<ProviderSegmentInfo>(() => {
    const modules = Array.from(new Set(exposures.map((e) => e.moduleKey).filter(Boolean)));
    const hasShop = modules.includes('shop');
    const hasBooking = modules.includes('booking');
    const hasHomeServices = modules.includes('home_services');
    const hasOnlineServices = modules.includes('online_services');
    const hasTeam = team.length > 0;
    const hasSpecialties = exposures.length > 0;
    const hasServices = serviceQueries.some((q) => (q.data?.length ?? 0) > 0);
    const hasProducts = productQueries.some((q) => (q.data?.length ?? 0) > 0);

    // Segment resolution priority: shop > salon > team > solo > unknown.
    // Shop wins when present because the dashboard surface (products, shop
    // orders) is markedly different from service ops. Salon wins next so
    // booking-first providers see Calendar prominently. Team is the default
    // for service businesses with workers; solo is a single-operator.
    let segment: ProviderSegment;
    if (!hasSpecialties) segment = 'unknown';
    else if (hasShop && !hasBooking && !hasHomeServices) segment = 'shop';
    else if (hasBooking) segment = 'salon';
    else if (hasTeam) segment = 'team';
    else segment = 'solo';

    return {
      segment,
      modules,
      hasShop,
      hasBooking,
      hasHomeServices,
      hasOnlineServices,
      hasTeam,
      hasSpecialties,
      hasServices,
      hasProducts,
      workerIds: team.map((member) => member.id),
      isLoading: isProviderAdmin && (loadingExp || loadingTeam),
      isError: isProviderAdmin && (exposureError || teamError),
      ready: !isProviderAdmin || (exposureFetched && teamFetched && !exposureError && !teamError),
    };
  }, [exposures, team, loadingExp, loadingTeam, exposureError, teamError, exposureFetched, teamFetched, serviceQueries, productQueries, isProviderAdmin]);
}
